import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './interceptor';


// ==================== ADMIN STATS ====================

// GET: /admin/stats/
export const useAdminStats = () =>
    useQuery({
        queryKey: ["admin_stats"],
        queryFn: () => api.get(`/admin/stats/`).then(res => res.data),
    });

// GET: /admin/top_services
export const useAdminTopServices = () =>
    useQuery({
        queryKey: ["admin_top_services"],
        queryFn: () => api.get(`/admin/top_services`).then(res => res.data),
    });


export const useAdminRecentTransactions = () =>
    useQuery({
        queryKey: ["admin_recent_transactions"],
        queryFn: () => api.get(`/admin/recent_transactions`).then(res => res.data),
    });



// ==================== ADMIN USERS ====================

// GET: /admin/users/
export const useAdminUsers = (params?: {
    search?: string;
    start_date?: string;
    end_date?: string;
}) =>
    useQuery({
        queryKey: ["admin_users_list", params],
        queryFn: () => {
            const queryParams = new URLSearchParams();
            if (params?.search) queryParams.append('search', params.search);
            if (params?.start_date) queryParams.append('start_date', params.start_date);
            if (params?.end_date) queryParams.append('end_date', params.end_date);

            const queryString = queryParams.toString();
            const url = queryString ? `/admin/users/?${queryString}` : `/admin/users/`;

            return api.get(url).then(res => res.data);
        },
    });

// GET: /admin/users/{id}/
export const useAdminUser = (id: number) =>
    useQuery({
        queryKey: ["admin_users_read", id],
        queryFn: () => api.get(`/admin/users/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// PUT: /admin/users/update/{id}/
export const useUpdateAdminUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/users/update/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_users_read"] });
        },
    });
};

// PATCH: /admin/users/update/{id}/
export const usePartialUpdateAdminUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.patch(`/admin/users/update/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_users_read"] });
        },
    });
};

// DELETE: /admin/users/delete/{id}/
export const useDeleteAdminUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/users/delete/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
        },
    });
};


// ==================== ADMIN TRANSACTIONS ====================

// GET: /admin/transactions/
export const useAdminTransactions = (params?: {
    search?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
}) =>
    useQuery({
        queryKey: ["admin_transactions_list", params],
        queryFn: () => {
            const queryParams = new URLSearchParams();
            if (params?.search) queryParams.append('search', params.search);
            if (params?.start_date) queryParams.append('start_date', params.start_date);
            if (params?.end_date) queryParams.append('end_date', params.end_date);
            if (params?.page) queryParams.append('page', params.page.toString());

            const queryString = queryParams.toString();
            const url = queryString ? `/admin/transactions/?${queryString}` : `/admin/transactions/`;

            return api.get(url).then(res => res.data);
        },
    });


// ==================== ADMIN WALLETS ====================

// GET: /admin/wallets
export const useAdminWallets = () =>
    useQuery({
        queryKey: ["admin_wallets_list"],
        queryFn: () => api.get(`/admin/wallets`).then(res => res.data),
    });

// GET: /admin/wallet/api_wallet
export const useAdminApiWallet = () =>
    useQuery({
        queryKey: ["admin_wallet_api_wallet"],
        queryFn: () => api.get(`/admin/wallet/api_wallet`).then(res => res.data),
    });

// PUT: /admin/manualfunding/
export const useAdminManualFunding = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/manualfunding/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_wallets_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
        },
    });
};


// ==================== ADMIN NETWORKS ====================

// GET: /admin/networks/
export const useAdminNetworks = () =>
    useQuery({
        queryKey: ["admin_networks_list"],
        queryFn: () => api.get(`/admin/networks/`).then(res => res.data),
    });

// GET: /admin/network/{id}/
export const useAdminNetwork = (id: number) =>
    useQuery({
        queryKey: ["admin_network_read", id],
        queryFn: () => api.get(`/admin/network/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// GET: /admin/networks/{id}/
export const useAdminNetworkById = (id: number) =>
    useQuery({
        queryKey: ["admin_networks_read", id],
        queryFn: () => api.get(`/admin/networks/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/networks/
export const useCreateAdminNetwork = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/networks/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
        },
    });
};

// POST: /admin/networks/{id}/
export const useCreateAdminNetworkById = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.post(`/admin/networks/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
        },
    });
};

// PUT: /admin/networks/
export const useUpdateAdminNetworks = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/networks/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
        },
    });
};

// PUT: /admin/networks/{id}/
export const useUpdateAdminNetworkById = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/networks/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_networks_read"] });
        },
    });
};

// DELETE: /admin/networks/
export const useDeleteAdminNetworks = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.delete(`/admin/networks/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
        },
    });
};

// DELETE: /admin/networks/{id}/
export const useDeleteAdminNetworkById = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/networks/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_networks_list"] });
        },
    });
};


// ==================== ADMIN DATA PLANS ====================

// GET: /admin/data/plans
export const useAdminDataPlans = () =>
    useQuery({
        queryKey: ["admin_data_plans_list"],
        queryFn: () => api.get(`/admin/data/plans`).then(res => res.data),
    });

// GET: /admin/data/planid/{id}/
export const useAdminDataPlan = (id: number) =>
    useQuery({
        queryKey: ["admin_data_planid_read", id],
        queryFn: () => api.get(`/admin/data/planid/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// GET: /admin/data/{id}/
export const useAdminData = (id: number) =>
    useQuery({
        queryKey: ["admin_data_read", id],
        queryFn: () => api.get(`/admin/data/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/data/plans
export const useCreateAdminDataPlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/data/plans/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
        },
    });
};

// POST: /admin/data/{id}/
export const useCreateAdminData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.post(`/admin/data/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
        },
    });
};

// PUT: /admin/data/plans
export const useUpdateAdminDataPlans = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/data/plans`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
        },
    });
};

// PUT: /admin/data/{id}/
export const useUpdateAdminData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/data/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_data_read"] });
        },
    });
};

// DELETE: /admin/data/plans
export const useDeleteAdminDataPlans = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.delete(`/admin/data/plans`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
        },
    });
};

// DELETE: /admin/data/{id}/
export const useDeleteAdminData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/data/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_data_plans_list"] });
        },
    });
};


// ==================== ADMIN CABLE ====================

// GET: /admin/cables/
export const useAdminCables = () =>
    useQuery({
        queryKey: ["admin_cables_list"],
        queryFn: () => api.get(`/admin/cables/`).then(res => res.data),
    });

// GET: /admin/cable/{id}/
export const useAdminCable = (id: number) =>
    useQuery({
        queryKey: ["admin_cable_read", id],
        queryFn: () => api.get(`/admin/cable/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/cables/
export const useCreateAdminCable = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/cables/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
        },
    });
};

// POST: /admin/cable/{id}/
export const useCreateAdminCableById = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.post(`/admin/cable/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
        },
    });
};

// PUT: /admin/cables/
export const useUpdateAdminCables = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/cables/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
        },
    });
};

// PUT: /admin/cable/{id}/
export const useUpdateAdminCable = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/cable/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_cable_read"] });
        },
    });
};

// DELETE: /admin/cables/
export const useDeleteAdminCables = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.delete(`/admin/cables/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
        },
    });
};

// DELETE: /admin/cable/{id}/
export const useDeleteAdminCable = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/cable/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cables_list"] });
        },
    });
};


// ==================== ADMIN CABLE PLANS ====================

// GET: /admin/cableplans/
export const useAdminCablePlans = () =>
    useQuery({
        queryKey: ["admin_cableplans_list"],
        queryFn: () => api.get(`/admin/cableplans/`).then(res => res.data),
    });

// GET: /admin/cable/plan/{id}/
export const useAdminCablePlan = (id: number) =>
    useQuery({
        queryKey: ["admin_cable_plan_read", id],
        queryFn: () => api.get(`/admin/cable/plan/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/cableplans/
export const useCreateAdminCablePlans = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/cableplans/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
        },
    });
};

// POST: /admin/cable/plan/{id}/
export const useCreateAdminCablePlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.post(`/admin/cable/plan/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
        },
    });
};

// PUT: /admin/cableplans/
export const useUpdateAdminCablePlans = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/cableplans/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
        },
    });
};

// PUT: /admin/cable/plan/{id}/
export const useUpdateAdminCablePlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/cable/plan/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_cable_plan_read"] });
        },
    });
};

// DELETE: /admin/cableplans/
export const useDeleteAdminCablePlans = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.delete(`/admin/cableplans/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
        },
    });
};

// DELETE: /admin/cable/plan/{id}/
export const useDeleteAdminCablePlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/cable/plan/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_cableplans_list"] });
        },
    });
};


// ==================== ADMIN EXAM PLANS ====================

// GET: /admin/exam/plans/
export const useAdminExamPlans = () =>
    useQuery({
        queryKey: ["admin_exam_plans_list"],
        queryFn: () => api.get(`/admin/exam/plans/`).then(res => res.data),
    });

// GET: /admin/exam/plan/{id}/
export const useAdminExamPlan = (id: number) =>
    useQuery({
        queryKey: ["admin_exam_plan_read", id],
        queryFn: () => api.get(`/admin/exam/plan/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/exam/plan/create/
export const useCreateAdminExamPlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/exam/plan/create/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_exam_plans_list"] });
        },
    });
};

// PUT: /admin/exam/plan/update/{id}/
export const useUpdateAdminExamPlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/exam/plan/update/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_exam_plans_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_exam_plan_read"] });
        },
    });
};

// DELETE: /admin/exam/plan/delete/{id}/
export const useDeleteAdminExamPlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/exam/plan/delete/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_exam_plans_list"] });
        },
    });
};


// ==================== ADMIN SERVICE SETTINGS ====================

// GET: /admin/service_settings/
export const useAdminServiceSettings = () =>
    useQuery({
        queryKey: ["admin_service_settings_list"],
        queryFn: () => api.get(`/admin/service_settings/`).then(res => res.data),
    });


// PUT: /admin/service_settings/update/
export const useUpdateAdminServiceSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/service_settings/update/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_service_settings_list"] });
        },
    });
};


// ==================== ADMIN API PROVIDERS ====================

// GET: /admin/api-providers/
export const useAdminApiProviders = () =>
    useQuery({
        queryKey: ["admin_api_providers_list"],
        queryFn: () => api.get(`/admin/api-providers/`).then(res => res.data),
    });

// GET: /admin/api-providers/{id}/
export const useAdminApiProvider = (id: number) =>
    useQuery({
        queryKey: ["admin_api_providers_read", id],
        queryFn: () => api.get(`/admin/api-providers/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// POST: /admin/api-providers/
export const useCreateAdminApiProvider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/api-providers/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_list"] });
        },
    });
};

// PUT: /admin/api-providers/{id}/
export const useUpdateAdminApiProvider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/api-providers/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_read"] });
        },
    });
};

// PATCH: /admin/api-providers/{id}/
export const usePartialUpdateAdminApiProvider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.patch(`/admin/api-providers/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_read"] });
        },
    });
};

// DELETE: /admin/api-providers/{id}/
export const useDeleteAdminApiProvider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/api-providers/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_list"] });
        },
    });
};

// POST: /admin/api-providers/{id}/toggle-status/
export const useToggleAdminApiProviderStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.post(`/admin/api-providers/${id}/toggle-status/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_api_providers_read"] });
        },
    });
};

// ==================== ADMIN PROVIDER SERVICES ====================

// GET: /admin/provider-services/
export const useAdminProviderServices = () =>
    useQuery({
        queryKey: ["admin_provider_services_list"],
        queryFn: () => api.get(`/admin/provider-services/`).then(res => res.data),
    });

// POST: /admin/provider-services/
export const useCreateAdminProviderService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.post(`/admin/provider-services/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_list"] });
        },
    });
};

// GET: /admin/provider-services/by-provider/{provider_id}/
export const useAdminProviderServicesByProvider = (providerId: number) =>
    useQuery({
        queryKey: ["admin_provider_services_by_provider", providerId],
        queryFn: () => api.get(`/admin/provider-services/by-provider/${providerId}/`).then(res => res.data),
        enabled: !!providerId,
    });

// GET: /admin/provider-services/{id}/
export const useAdminProviderService = (id: number) =>
    useQuery({
        queryKey: ["admin_provider_services_read", id],
        queryFn: () => api.get(`/admin/provider-services/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// PUT: /admin/provider-services/{id}/
export const useUpdateAdminProviderService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/provider-services/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_read"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_by_provider"] });
        },
    });
};

// PATCH: /admin/provider-services/{id}/
export const usePartialUpdateAdminProviderService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.patch(`/admin/provider-services/${id}/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_read"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_by_provider"] });
        },
    });
};

// DELETE: /admin/provider-services/{id}/
export const useDeleteAdminProviderService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.delete(`/admin/provider-services/${id}/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_by_provider"] });
        },
    });
};

// POST: /admin/provider-services/{id}/toggle-status/
export const useToggleAdminProviderServiceStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            api.post(`/admin/provider-services/${id}/toggle-status/`).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_read"] });
            queryClient.invalidateQueries({ queryKey: ["admin_provider_services_by_provider"] });
        },
    });
};

// ==================== ADMIN LOAN SETTINGS ====================

// GET: /admin/loan-settings/
export const useAdminLoanSettings = () =>
    useQuery({
        queryKey: ["admin_loan_settings_list"],
        queryFn: () => api.get(`/admin/loan-settings/`).then(res => res.data),
    });

// PUT: /admin/loan-settings/update/
export const useUpdateAdminLoanSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.put(`/admin/loan-settings/update/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_loan_settings_list"] });
        },
    });
};

// PATCH: /admin/loan-settings/update/
export const usePartialUpdateAdminLoanSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) =>
            api.patch(`/admin/loan-settings/update/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_loan_settings_list"] });
        },
    });
};

// ==================== ADMIN LOANS ====================

// GET: /admin/loans/
export const useAdminLoans = () =>
    useQuery({
        queryKey: ["admin_loans_list"],
        queryFn: () => api.get(`/admin/loans/`).then(res => res.data),
    });

// GET: /admin/loans/{id}/
export const useAdminLoan = (id: number) =>
    useQuery({
        queryKey: ["admin_loans_read", id],
        queryFn: () => api.get(`/admin/loans/${id}/`).then(res => res.data),
        enabled: !!id,
    });

// PUT: /admin/loans/{id}/update-status/
export const useUpdateAdminLoanStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.put(`/admin/loans/${id}/update-status/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_loans_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_loans_read"] });
        },
    });
};

// PATCH: /admin/loans/{id}/update-status/
export const usePartialUpdateAdminLoanStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) =>
            api.patch(`/admin/loans/${id}/update-status/`, data).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin_loans_list"] });
            queryClient.invalidateQueries({ queryKey: ["admin_loans_read"] });
        },
    });
};
